import React, { useMemo, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Search, Pill, User, Mail, Paperclip, Save } from "lucide-react";
import { toast } from "sonner";
import { PrescriptionChat } from "@/components/PrescriptionChat";

const STATUSES = ["submitted", "approved", "info_required", "lab_required", "followup_required", "rejected"];
const LABELS = {
  submitted: "Submitted", approved: "Approved", info_required: "Information required",
  lab_required: "Lab result required", followup_required: "Follow-up needed", rejected: "Not approved",
};
const statusColor = (s) =>
  s === "approved" ? "bg-champagne-gold text-deep-ink"
  : s === "rejected" ? "bg-deep-ink/10 text-deep-ink/50"
  : s === "submitted" ? "bg-muted-sage/30 text-deep-ink"
  : "bg-terracotta/15 text-terracotta";
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export const PrescriptionsManager = ({ prescriptions, onUpdate }) => {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState({});

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return prescriptions.filter((p) => {
      if (filter !== "all" && p.status !== filter) return false;
      if (!term) return true;
      return [p.patient_name, p.patient_email, p.medication].some((v) => (v || "").toLowerCase().includes(term));
    });
  }, [prescriptions, q, filter]);

  const open = (p) => { setSel(p); setEdit({ status: p.status, status_note: p.status_note || "" }); };

  const save = async () => {
    const patch = {};
    if (edit.status !== sel.status) patch.status = edit.status;
    if ((edit.status_note || "") !== (sel.status_note || "")) patch.status_note = edit.status_note;
    if (Object.keys(patch).length === 0) { toast.info("No changes to save"); return; }
    await onUpdate(sel.id, patch);
    setSel(null);
  };

  const viewDoc = async (fileId) => {
    try {
      const res = await fetch(`${API}/files/${fileId}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch { toast.error("Could not open document"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-deep-ink/40" />
          <Input data-testid="rx-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient, email, medication…" className="bg-warm-ivory ps-9" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52 bg-warm-ivory" data-testid="rx-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 overflow-hidden rounded-3xl bg-warm-ivory pb-2">
        {filtered.map((p) => (
          <button key={p.id} onClick={() => open(p)} data-testid={`rx-row-${p.id}`}
            className="flex w-full items-center gap-4 border-t border-border/60 px-4 py-3 text-start transition-colors hover:bg-sand/60">
            <div className="w-40 flex-shrink-0">
              <p className="truncate text-sm font-medium text-deep-ink">{p.patient_name || "—"}</p>
              <p className="truncate text-xs text-deep-ink/50">{p.patient_email}</p>
            </div>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 truncate text-sm text-deep-ink"><Pill className="h-3.5 w-3.5 text-champagne-gold" />{p.medication}{p.dosage ? ` · ${p.dosage}` : ""}</p>
              <p className="text-xs text-deep-ink/40">{fmtDate(p.created_at)}</p>
            </div>
            {p.document_id && <Paperclip className="h-4 w-4 flex-shrink-0 text-deep-ink/40" />}
            <Badge className={`${statusColor(p.status)} flex-shrink-0`}>{LABELS[p.status]}</Badge>
          </button>
        ))}
        {filtered.length === 0 && <p className="p-8 text-center text-deep-ink/50">No prescription requests.</p>}
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="rx-detail-dialog">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-deep-ink"><Pill className="h-5 w-5 text-champagne-gold" />{sel.medication}</DialogTitle>
                <DialogDescription className="text-sm text-deep-ink/60">{sel.dosage || "—"}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="rounded-2xl bg-sand p-4">
                  <p className="flex items-center gap-2 text-sm font-medium text-deep-ink"><User className="h-4 w-4 text-champagne-gold" />{sel.patient_name}</p>
                  <p className="mt-1 flex items-center gap-2 text-sm text-deep-ink/70"><Mail className="h-3.5 w-3.5" />{sel.patient_email}</p>
                  {sel.reason && <p className="mt-2 text-sm text-deep-ink/70"><span className="text-deep-ink/40">Reason: </span>{sel.reason}</p>}
                  <p className="mt-2 text-xs text-deep-ink/40">Requested {fmtDate(sel.created_at)}</p>
                  {sel.document_id && (
                    <button onClick={() => viewDoc(sel.document_id)} data-testid="rx-view-doc" className="mt-2 inline-flex items-center gap-1.5 text-sm text-champagne-gold hover:underline">
                      <Paperclip className="h-3.5 w-3.5" />View attached document
                    </button>
                  )}
                </div>
                <div>
                  <p className="mb-1.5 text-sm text-deep-ink/70">Status</p>
                  <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                    <SelectTrigger className={`border-0 ${statusColor(edit.status)}`} data-testid="rx-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="mb-1.5 text-sm text-deep-ink/70">Note to patient</p>
                  <Textarea data-testid="rx-note" rows={3} value={edit.status_note} onChange={(e) => setEdit({ ...edit, status_note: e.target.value })} placeholder="e.g. Please book a follow-up appointment first." className="bg-sand" />
                </div>
                <div>
                  <p className="mb-1.5 text-sm text-deep-ink/70">Messages with patient</p>
                  <PrescriptionChat prescriptionId={sel.id} staff />
                </div>
              </div>
              <DialogFooter className="mt-2">
                <Button onClick={save} data-testid="rx-save" className="w-full gap-2 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink">
                  <Save className="h-4 w-4" />Save & notify patient
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
